import React from "react";
import PageWithSidebar from "components/PageWithSidebar";
import { Card, Container, Divider, Header, Icon } from "semantic-ui-react";
import { Link, Route, Routes } from "react-router";

import RestApis from "./RestApis";
import SideNav from "./SideNav";
import WebSocketApis from "./WebSocketApis";
import EventBasedApis from "./EventBasedApis";
import PrivateApis from "./PrivateApis";
import EndpointSecurity from "./EndpointSecurity";
import ElkMonitoring from "./ElkMonitoring";
import WebsocketTestClient from "./WebsocketTestClient";
import JsonYamlEditor from "./JsonYamlEditor";
import Editor from "./Editor";
import APIGWSelfService from "./APIGWSelfService";
import {
  EventBasedRoute,
  DeveloperToolsRoute,
  EndpointSecurityRoute,
  RegisteredRoute,
  RestRoute,
  WsRoute,
  IkanoUserRoute,
} from "index";
import {
  isAuthenticated,
  isIkanoUser,
  isDeveloperToolsUser,
  isRestAPIUser,
  isWsAPIUser,
} from "services/self";

function Documentation() {
  return (
    <PageWithSidebar sidebarContent={<SideNav />}>
      <Routes>
        <Route end path="/rest-apis" element={<RestRoute><RestApis/></RestRoute>} />
        <Route end
          path="/websocket-apis"
          element={<WsRoute><WebSocketApis/></WsRoute>}
        />
        <Route end
          path="/event-based-apis"
          element={<EventBasedRoute><EventBasedApis/></EventBasedRoute>}
        />
        <Route end
          path="/private-apis"
          element={<IkanoUserRoute><PrivateApis/></IkanoUserRoute>}
        />
        <Route end
          path="/endpoint-security"
          element={<EndpointSecurityRoute><EndpointSecurity/></EndpointSecurityRoute>}
        />
        <Route end
          path="/elk-monitoring"
          element={<IkanoUserRoute><ElkMonitoring/></IkanoUserRoute>}
        />
        <Route end
          path="/websocket-test-client"
          element={<DeveloperToolsRoute><WebsocketTestClient/></DeveloperToolsRoute>}
        />
        <Route end
          path="/api-gw-self-service"
          element={<DeveloperToolsRoute><APIGWSelfService/></DeveloperToolsRoute>}
        />
        <Route end
          path="/api-specification-editor"
          element={<DeveloperToolsRoute><Editor/></DeveloperToolsRoute>}
        />
        <Route end
          path="/json-yaml-editor" 
          element={<RegisteredRoute><JsonYamlEditor/></RegisteredRoute>}
        />
      <Route path="/"
        element={<Container fluid>
          <Header as="h2" textAlign="center">
            Welcome to the documentation of the Ikano API Portal
          </Header>
          <Header dividing>Introduction</Header>
          This documentation will guide and provide you with knowledge about the
          features of the Ikano API Portal. Click on the below cards to know
          more about each of the features.
          <br />
          <br />
          <Card.Group centered>
            {isRestAPIUser() && (
              <Card as={Link} to="/documentation/rest-apis">
                <Card.Content>
                  <Card.Header>REST APIs</Card.Header>
                  <Card.Description textAlign="center">
                    Comprehensive information about REST API endpoints,
                    authentication methods, request/response formats, and usage
                    guidelines, all in one place.
                  </Card.Description>
                  <Card.Meta textAlign="center">
                    <Icon
                      name="arrow circle right"
                      color="black"
                      link
                      size="big"
                    />
                  </Card.Meta>
                </Card.Content>
              </Card>
            )}
            {isWsAPIUser() && (
              <Card as={Link} to="/documentation/websocket-apis">
                <Card.Content>
                  <Card.Header>WebSocket APIs</Card.Header>
                  <Card.Description textAlign="center">
                    Comprehensive information about the WebSocket routes,
                    authentication methods, request/response formats, and usage
                    guidelines, all in one place.
                  </Card.Description>
                  <Card.Meta textAlign="center">
                    <Icon
                      name="arrow circle right"
                      color="black"
                      link
                      size="big"
                    />
                  </Card.Meta>
                </Card.Content>
              </Card>
            )}
            {isIkanoUser() && (
              <Card as={Link} to="/documentation/event-based-apis">
                <Card.Content>
                  <Card.Header>Event based APIs</Card.Header>
                  <Card.Description textAlign="center">
                    Comprehensive information about the Kafka based
                    Microservices, brokers, request/response formats, and usage
                    guidelines, all in one place.
                  </Card.Description>
                  <Card.Meta textAlign="center">
                    <Icon
                      name="arrow circle right"
                      color="black"
                      link
                      size="big"
                    />
                  </Card.Meta>
                </Card.Content>
              </Card>
            )}
            {isIkanoUser() && (
              <Card as={Link} to="/documentation/private-apis">
                <Card.Content>
                  <Card.Header>Private APIs</Card.Header>
                  <Card.Description textAlign="center">
                    Internal API documentation accessible exclusively to Ikano
                    users for proprietary services and integrations.
                  </Card.Description>
                  <Card.Meta textAlign="center">
                    <Icon
                      name="arrow circle right"
                      color="black"
                      link
                      size="big"
                    />
                  </Card.Meta>
                </Card.Content>
              </Card>
            )}
            {(isRestAPIUser() || isWsAPIUser()) && (
              <Card as={Link} to="/documentation/endpoint-security">
                <Card.Content>
                  <Card.Header>Endpoint Security</Card.Header>
                  <Card.Description textAlign="center">
                    Comprehensive information about about the security
                    information of all the REST endpoints and the WebSocket
                    routes all in one place.
                  </Card.Description>
                  <Card.Meta textAlign="center">
                    <Icon
                      name="arrow circle right"
                      color="black"
                      link
                      size="big"
                    />
                  </Card.Meta>
                </Card.Content>
              </Card>
            )}
            {isIkanoUser() && (
              <Card as={Link} to="/documentation/elk-monitoring">
                <Card.Content>
                  <Card.Header>ELK Monitoring</Card.Header>
                  <Card.Description textAlign="center">
                    Comprehensive information about dashboards and alerts
                    configured in ELK for API Gateway REST and Websocket
                    endpoints.
                  </Card.Description>
                  <Card.Meta textAlign="center">
                    <Icon
                      name="arrow circle right"
                      color="black"
                      link
                      size="big"
                    />
                  </Card.Meta>
                </Card.Content>
              </Card>
            )}
          </Card.Group>
          <Divider />
          {isAuthenticated() && <Header>Developer Tools</Header>}
          <Card.Group centered>
            {isAuthenticated() && (
              <Card as={Link} to="/documentation/websocket-test-client">
                <Card.Content>
                  <Card.Header>WebSocket Test Client</Card.Header>
                  <Card.Description textAlign="center">
                    Interactive WebSocket test client interface which allows
                    developers to connect, send and receive in real time through
                    a WebSocket connection.
                  </Card.Description>
                  <Card.Meta textAlign="center">
                    <Icon
                      name="arrow circle right"
                      color="black"
                      link
                      size="big"
                    />
                  </Card.Meta>
                </Card.Content>
              </Card>
            )}
            {isDeveloperToolsUser() && (
              <Card as={Link} to="/documentation/api-gw-self-service">
                <Card.Content>
                  <Card.Header>APIGW Self-Service</Card.Header>
                  <Card.Description textAlign="center">
                    Self-service UI that allows developers from different
                    modules to create, update, delete and promote REST endpoints
                    and WebSocket routes.
                  </Card.Description>
                  <Card.Meta textAlign="center">
                    <Icon
                      name="arrow circle right"
                      color="black"
                      link
                      size="big"
                    />
                  </Card.Meta>
                </Card.Content>
              </Card>
            )}
            {isDeveloperToolsUser() && (
              <Card as={Link} to="/documentation/api-specification-editor">
                <Card.Content>
                  <Card.Header>API Specification Editor</Card.Header>
                  <Card.Description textAlign="center">
                    Powerful tool designed to simplify the development of API
                    documentation. It allows developers to create, edit, and
                    validate API specifications in real-time
                  </Card.Description>
                  <Card.Meta textAlign="center">
                    <Icon
                      name="arrow circle right"
                      color="black"
                      link
                      size="big"
                    />
                  </Card.Meta>
                </Card.Content>
              </Card>
            )}
            {isAuthenticated() && (
              <Card as={Link} to="/documentation/json-yaml-editor">
                <Card.Content>
                  <Card.Header>JSON/YAML Editor</Card.Header>
                  <Card.Description textAlign="center">
                    Editor designed to help developers working with JSON/YAML
                    documents. It allows to easily validate, prettify, compress
                    or copy documents.
                  </Card.Description>
                  <Card.Meta textAlign="center">
                    <Icon
                      name="arrow circle right"
                      color="black"
                      link
                      size="big"
                    />
                  </Card.Meta>
                </Card.Content>
              </Card>
            )}
          </Card.Group>
        </Container>}/>
        </Routes>
    </PageWithSidebar>
    
  );
}

export default Documentation;
